/**
 * The one place the screen touches storage and sync.
 *
 * Its purpose is to make the ordering un-bypassable: **build the event, commit it to disk,
 * and only then let React render.** Every mutation on the scoring screen goes through
 * `record`, so there is no code path where a ball reaches the UI before it is durable, and
 * no opportunity for a later change to add one by accident.
 *
 * The draft in React state is therefore never *ahead* of disk. If the tab dies between the
 * write and the render, the reload finds the ball.
 *
 * Two invariants are maintained here and are easy to lose:
 *
 * - **Writes are serialised, and drains are not on that queue.** Two taps in the same tick
 *   must not both read the same draft and assign the same `seq`, so writes go through a
 *   promise chain. A *drain* must never join it: the outbox retries with backoff until it
 *   succeeds, so while the network is down `drain()` does not resolve for minutes, and
 *   chaining it would block the next ball behind the network. That is precisely backwards
 *   for a local-first app, and it is the bug the browser drive caught.
 * - **Nothing here listens for the network.** The app-level drain (`sync/foreground.ts`) owns
 *   that, for every match and every screen; this only kicks a drain after a write and watches.
 * - **The draft mirrors disk after every drain.** A `seq_conflict` renumbers an event *on
 *   disk*; if the draft kept the old number it would hand out a colliding address on the
 *   next ball and conflict again, converging only after needless churn.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
// Aliased because this file is compiled with the DOM lib, where `Event` is the browser's.
import type { Event as LedgerEvent, EventAck, MatchId, SeqGap } from "@sportsos/engine";
import type { Drainer, EventStore, SyncState } from "@sportsos/sync";
import { requestBackgroundDrain } from "../sync/background.ts";
import type { MatchDraft } from "./draft.ts";
import { emptyDraft, withRecords } from "./draft.ts";
import type { MatchSetup } from "./setup.ts";

/** A ball the server refused for a reason retrying cannot fix. Shown, never swallowed. */
export interface StuckEvent {
  readonly event: LedgerEvent;
  readonly ack: EventAck;
}

export interface Ledger {
  readonly draft: MatchDraft;
  readonly sync: SyncState;
  /** Holes the server has reported. Rendered as a visible error (PRD §7.2). */
  readonly gaps: readonly SeqGap[];
  readonly stuck: readonly StuckEvent[];
  /** True until the ledger has been read back off disk. */
  readonly hydrating: boolean;
  /**
   * Durably store one or more events, then fold them into the draft.
   *
   * `build` receives the draft as it really is on disk, so `seq` is assigned against the
   * truth. Returning null or an empty list means "nothing to record" — callers that can
   * legitimately have nothing to do say so rather than fabricating an event.
   *
   * A list, because undo is not one event: voiding a wicket also voids the batter who came
   * in after it, and either both land or the ledger describes a state that never happened.
   */
  readonly record: (
    build: (draft: MatchDraft) => LedgerEvent | readonly LedgerEvent[] | null,
  ) => Promise<void>;
  /**
   * Delete the unsent balls another account wrote. Only for the scorer's explicit choice, after
   * a confirmation — the one path that removes anything from the ledger on this device.
   */
  readonly discardForeign: () => Promise<void>;
  /** Update the match setup (e.g. changing the keeper mid-match). Local only. */
  readonly updateSetup: (patch: (setup: MatchSetup) => MatchSetup) => void;
}

export interface LedgerOptions {
  readonly setup: MatchSetup;
  readonly store: EventStore;
  /**
   * The page's one drainer. This screen watches its match rather than owning an outbox, so the
   * pill reports what the app-level drain is doing — including a drain that ran while this
   * screen was closed — and there is one drainer per match instead of two fighting over it.
   */
  readonly drainer: Drainer;
  /** Seed events for a brand-new match — the opening `InningsStart`. */
  readonly seed?: (draft: MatchDraft) => readonly LedgerEvent[];
}

export function useLedger({ setup, store, drainer, seed }: LedgerOptions): Ledger {
  const matchId: MatchId = setup.matchId;

  const [draft, setDraft] = useState<MatchDraft>(() => emptyDraft(setup));
  const [sync, setSync] = useState<SyncState>({ kind: "idle" });
  const [gaps, setGaps] = useState<readonly SeqGap[]>([]);
  const [stuck, setStuck] = useState<readonly StuckEvent[]>([]);
  const [hydrating, setHydrating] = useState(true);

  /** The authoritative in-memory copy. Updated synchronously so queued writes see it. */
  const draftRef = useRef<MatchDraft>(emptyDraft(setup));
  /** Serialises writes, so two taps in one tick cannot claim the same address. */
  const queue = useRef<Promise<unknown>>(Promise.resolve());

  const outbox = useMemo(() => drainer.outbox(matchId), [drainer, matchId]);

  /** Re-read disk into the draft. The draft is a mirror, never a separate truth. */
  const mirror = useCallback(async (): Promise<void> => {
    const rows = await store.getAll(matchId);
    draftRef.current = withRecords(draftRef.current, rows);
    setDraft(draftRef.current);
  }, [store, matchId]);

  /**
   * Kick the outbox, off the write queue.
   *
   * Re-entrant on the `Outbox` side, so calling it on every ball does not stack up parallel
   * drains — the running one simply loops once more. Failures are the outbox's business.
   */
  const startDrain = useCallback((): void => {
    void outbox.drain().catch(() => undefined);
    // Also ask the browser to drain in the background, for the case where this page is closed
    // before the network comes back. A no-op where unsupported (iOS), where the foreground
    // drain is all there is.
    void requestBackgroundDrain();
  }, [outbox]);

  /**
   * Hear what the drainer is doing with this match, whoever started it.
   *
   * The draft is re-read from disk whenever a drain settles, because a `seq_conflict`
   * renumbers an event *on disk* and a draft that kept the old number would hand out a
   * colliding address on the next ball. That has to hold for a drain the app-level timer
   * started as much as for one this screen kicked.
   */
  useEffect(
    () =>
      drainer.watch(matchId, {
        onStateChange: (state) => {
          setSync(state);
          if (state.kind !== "syncing") void mirror().catch(() => undefined);
        },
        // Accumulated rather than replaced: a hole reported once and then overwritten by a
        // later clean response would vanish off the screen, and PRD §7.2 wants it to stay
        // visible until a human deals with it.
        onGaps: (reported) =>
          setGaps((current) => {
            const key = (g: SeqGap): string => `${g.inningsNo}:${g.fromSeq}-${g.toSeq}`;
            const seen = new Set(current.map(key));
            const fresh = reported.filter((g) => !seen.has(key(g)));
            return fresh.length === 0 ? current : [...current, ...fresh];
          }),
        onStuck: (event, ack) =>
          setStuck((current) =>
            current.some((entry) => entry.event.eventId === event.eventId)
              ? current
              : [...current, { event, ack }],
          ),
      }),
    [drainer, matchId, mirror],
  );

  /** Guards the double-invoked effect React runs in development. */
  const hydrated = useRef(false);

  useEffect(() => {
    if (hydrated.current) return;
    hydrated.current = true;

    queue.current = queue.current.then(async () => {
      const existing = await store.getAll(matchId);
      if (existing.length === 0 && seed !== undefined) {
        // A brand-new match. The opening events go through disk like every other event.
        for (const event of seed(emptyDraft(setup))) {
          await store.append(matchId, event);
        }
      }
      // Either way the ledger on disk *is* the match, so the screen adopts it wholesale
      // rather than trusting anything it held in memory.
      await mirror();
      setHydrating(false);
      await outbox.refresh();
      // Deliberately not awaited. See the note at the top of the file.
      startDrain();
    });
    // Captured once on purpose: hydration happens exactly once per match, and re-running it
    // would re-seed a match that already exists on disk.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [store, matchId, outbox, mirror, startDrain]);

  const record = useCallback(
    (build: (current: MatchDraft) => LedgerEvent | readonly LedgerEvent[] | null): Promise<void> => {
      const task = queue.current.then(async () => {
        const built = build(draftRef.current);
        const events = built === null ? [] : Array.isArray(built) ? built : [built as LedgerEvent];
        if (events.length === 0) return;

        // The line the whole ticket is about: nothing is shown until every one of these is
        // durable. Written in order, so a crash part-way leaves a prefix — which replays to
        // a real state — rather than a hole.
        for (const event of events) {
          await store.append(matchId, event);
        }
        await mirror();

        // A ball is safe the moment it is on disk. Sending is a separate concern and must
        // never gate the next tap.
        startDrain();
      });
      queue.current = task.catch(() => undefined);
      return task;
    },
    [store, matchId, mirror, startDrain],
  );

  const updateSetup = useCallback(
    (patch: (setup: MatchSetup) => MatchSetup): void => {
      setDraft((current) => {
        const updated = { ...current, setup: patch(current.setup) };
        draftRef.current = updated;
        return updated;
      });
    },
    [],
  );

  const discardForeign = useCallback(async (): Promise<void> => {
    // On the write queue, so it cannot interleave with a ball being recorded.
    const task = queue.current.then(async () => {
      await outbox.discardForeign();
      await mirror();
    });
    queue.current = task.catch(() => undefined);
    await task;
  }, [outbox, mirror]);

  return { draft, sync, gaps, stuck, hydrating, record, discardForeign, updateSetup };
}
